import { makeAutoObservable, computed, action } from "mobx";
import type {
  ActionReference,
  LoopRecordListActionGroupConfig,
} from "@smartsuite/automation-engine-api";
import * as R from "remeda";
import type {
  ActionCandidate,
  ActionGroupCandidate,
  AutomationCandidate,
  LoopActionGroup,
} from "../../types/candidate";
import type { CandidateStore } from "./candidate.store";
import type { ActionListMerger } from "./mergers/actions";
import { ejectReferences } from "./utils/filters";
import {
  collectActionsDeep,
  collectInstanceIdsDeep,
  findGroupByInstanceId,
  locateEnclosingLoop,
  locateEnclosingLoopForGroup,
  mapScopeActionsDeep,
} from "./utils/action-group-walk";
import {
  compareActionReferences,
  recordListInputConfigToOptionValue,
} from "../../utils/conditions-coverters";
import { getOutputDescriptions } from "../../utils/conditions-coverter.hook";
import { ActionLabel, CURRENT_ITEM_OUTPUT_ID } from "../../utils/constants";
import { findRecordsActionsToOptions } from "../../containers/AutomationForm/Forms/InputForm/RecordsSourceEditor/utils";
import { t } from "i18next";
import { outputReferenceToOptionValue } from "../../containers/AutomationForm/Sections/LoopingSection/components/SelectWebhookOutput/get-available-webhook-outputs";

/**
 * Read surface for a single loop's scope-local data -- the same shape as the
 * root-singleton getters (`source`, `loopActions`, `loopName`,
 * `isConfiguredSourceValid`, `getDefaultGroupName()`), but resolved against
 * one specific loop instanceId instead of always the first root-level loop.
 * `instanceId` is the addressed loop's own id, so a consumer holding a scope
 * can tell which loop it's reading without threading the id separately.
 */
// eslint-disable-next-line import/no-unused-modules
export type LoopScope = Pick<
  LoopingStore,
  "source" | "loopActions" | "loopName" | "isConfiguredSourceValid"
> & {
  getDefaultGroupName: () => ReturnType<LoopingStore["getDefaultGroupName"]>;
  instanceId?: number;
};

export class LoopingStore {
  private readonly candidateStore: CandidateStore;

  /**
   * One stable LoopScope object per loop instanceId. Consumers put the scope
   * into React hook deps (useEffect/useMemo); a fresh object per scopeFor
   * call makes setState-effects fire on every render — an infinite render
   * loop for any editor whose action sits inside a loop. The scope's getters
   * read live store state, so caching the object itself is safe.
   *
   * Not `private` only because the MobX annotation below must name it to
   * keep it non-observable, and TS excludes private fields from `keyof`.
   */
  readonly scopeCache = new Map<number, LoopScope>();

  constructor(candidateStore: CandidateStore) {
    this.candidateStore = candidateStore;
    makeAutoObservable(
      this,
      {
        loopName: computed,
        source: computed,
        loopActions: computed,
        self: computed,
        // plain identity cache, written during render — must not be observable
        scopeCache: false,
      },
      { autoBind: true },
    );
  }

  /** Gets the automation candidate */
  get self(): AutomationCandidate {
    return this.candidateStore.self;
  }

  /** Gets the loop action group from the automation's action groups */
  private get loopActionGroup(): LoopActionGroup | undefined {
    return this.self.actionGroups.find(
      (actionGroup) => actionGroup.config.oneofKind === "loop",
    ) as LoopActionGroup | undefined;
  }

  /** Loop group addressed by instanceId, anywhere in the tree (nested loops). */
  loopGroupById(instanceId: number): LoopActionGroup | undefined {
    const group = findGroupByInstanceId(this.self.actionGroups, instanceId);
    return group?.config.oneofKind === "loop"
      ? (group as LoopActionGroup)
      : undefined;
  }

  /** Gets the record list configuration of the loop addressed by instanceId. */
  sourceAt(
    instanceId: number,
  ): LoopRecordListActionGroupConfig["source"] | undefined {
    return this.loopGroupById(instanceId)?.config.loop.source;
  }

  /** Sets the record list configuration of the loop addressed by instanceId. */
  @action
  setSourceAt(
    instanceId: number,
    source?: LoopRecordListActionGroupConfig["source"],
  ): void {
    const loopGroup = this.loopGroupById(instanceId);
    if (loopGroup === undefined) return;

    loopGroup.config.loop.source = source;
  }

  /** Gets the name of the loop addressed by instanceId. */
  loopNameAt(instanceId: number): string {
    return this.loopGroupById(instanceId)?.config.loop.name ?? "";
  }

  /** Sets the name of the loop addressed by instanceId. */
  @action
  setLoopNameAt(instanceId: number, name: string): void {
    const loopGroup = this.loopGroupById(instanceId);
    if (loopGroup === undefined) return;

    loopGroup.config.loop.name = name;
  }

  /** Gets the actions of the loop addressed by instanceId (deep, own "actions" groups only). */
  loopActionsAt(instanceId: number): ActionCandidate[] {
    const loopGroup = this.loopGroupById(instanceId);
    if (loopGroup === undefined) return [];

    return collectActionsDeep(
      loopGroup.config.loop.actionGroups.filter(
        (group) => group.config.oneofKind === "actions",
      ),
    );
  }

  /**
   * Loop scope for the loop group with the given instanceId: same read
   * surface as the root-singleton getters, but resolved via the `*At`
   * accessors so every property stays live (each access re-reads the
   * observable state through the accessor, it isn't a snapshot taken here).
   * Returns undefined when instanceId doesn't address a loop group.
   */
  scopeFor(instanceId: number): LoopScope | undefined {
    if (this.loopGroupById(instanceId) === undefined) return undefined;

    const cached = this.scopeCache.get(instanceId);
    if (cached !== undefined) return cached;

    // `store` stands in for `this` inside the getters below: an
    // object-literal `get x() {}` is bound to the object it's declared on,
    // not this method's `this`, so the getters read through `store` (called
    // as `store.method(...)`, never extracted bare) to reach the `*At`
    // accessors live on every access -- not a snapshot taken here.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const store = this;
    const scope: LoopScope = {
      instanceId,
      get source() {
        return store.sourceAt(instanceId);
      },
      get loopActions() {
        return store.loopActionsAt(instanceId);
      },
      get loopName() {
        return store.loopNameAt(instanceId);
      },
      get isConfiguredSourceValid() {
        return store.isConfiguredSourceValidAt(instanceId);
      },
      getDefaultGroupName: () => store.defaultGroupNameAt(instanceId),
    };
    this.scopeCache.set(instanceId, scope);
    return scope;
  }

  /**
   * Loop scope for the loop CONTAINING the given action, at any nesting
   * depth. Root-loop actions get the root loop's scope; an action inside a
   * branch that itself sits inside a loop resolves to that enclosing loop
   * (locateEnclosingLoop climbs past branch containers). Actions not inside
   * any loop (root-level, or inside a branch with no enclosing loop) return
   * undefined.
   */
  scopeForAction(actionHash: string): LoopScope | undefined {
    const loopInstanceId = locateEnclosingLoop(
      this.self.actionGroups,
      actionHash,
    );
    return loopInstanceId !== undefined
      ? this.scopeFor(loopInstanceId)
      : undefined;
  }

  /**
   * Loop scope for the loop CONTAINING the block (loop/branches/actions
   * group) with the given instanceId, at any nesting depth -- the
   * group-addressed twin of `scopeForAction`. A branch block sitting inside a
   * loop (directly or via nested branches) resolves to that enclosing loop's
   * Current-Item scope; a block not inside any loop returns undefined. Used to
   * offer "Current Item" as a left-side source for a branch condition when the
   * branch block lives inside a Looping block.
   */
  scopeForGroup(instanceId: number): LoopScope | undefined {
    const loopInstanceId = locateEnclosingLoopForGroup(
      this.self.actionGroups,
      instanceId,
    );
    return loopInstanceId !== undefined
      ? this.scopeFor(loopInstanceId)
      : undefined;
  }

  /**
   * Adds new action groups to the automation
   * @param actionGroups - Array of action groups to add
   */
  @action
  setActionGroups(actionGroups: ActionGroupCandidate[]): void {
    this.self.actionGroups = [...this.self.actionGroups, ...actionGroups];
  }

  /**
   * Deletes an action group by its hash
   * @param actionGroupHash - Hash of the action group to delete
   */
  @action
  deleteActionGroup(actionGroupHash: number): void {
    this.self.actionGroups = this.self.actionGroups.filter(
      (actionGroup) => actionGroup.instanceId !== actionGroupHash,
    );
  }

  /**
   * Sets the actions for the loop action group
   * @param actions - Array of actions to set
   */
  @action
  setLoopActions(actions: ActionCandidate[]): void {
    const loopGroup = this.loopActionGroup;
    if (!this.isValidLoopActionGroup(loopGroup)) return;

    loopGroup.config.loop.actionGroups[0].config.actions.actions = actions;
  }

  /**
   * Applies action list mergers to the loop actions
   * @param mergers - Array of action list merger functions to apply
   */
  @action
  setActions(...mergers: ActionListMerger[]): void {
    const actions = R.reduce<ActionListMerger, ActionCandidate[]>(
      mergers,
      (actions, merger) => merger(actions),
      this.loopActions,
    );

    this.setLoopActions(actions);
  }

  /**
   * Sets the record list configuration for the loop
   * @param recordList - Record list input configuration
   */
  @action
  setSource(source?: LoopRecordListActionGroupConfig["source"]): void {
    const loopGroup = this.loopActionGroup;
    if (!this.isValidLoopActionGroup(loopGroup)) return;

    loopGroup.config.loop.source = source;
  }

  @action
  clearSourceIfTriggerReference(): void {
    for (const id of this.allLoopInstanceIds()) {
      const source = this.sourceAt(id);
      if (
        source?.oneofKind === "outputReference" &&
        source.outputReference.source.oneofKind === "triggerReference"
      ) {
        this.setSourceAt(id, undefined);
        // currentItem pills inside the loop derive from the trigger output;
        // wipe all triggerReferences from loop actions since their schema is gone.
        this.setActionsWithOutReferencesAt(id, "triggerReference");
      }
    }
  }

  /**
   * The Find Records action a loop runs over now searches another table:
   * the copy of that table the loop's source carries follows it
   * (BUG-22529), in every loop that runs over that action.
   */
  @action
  followSourceTable(
    sourceInstanceId: number,
    table: { applicationId: string; solutionId?: string },
  ): void {
    for (const id of this.allLoopInstanceIds()) {
      const source = this.sourceAt(id);
      if (
        source?.oneofKind !== "recordList" ||
        source.recordList.recordList.oneofKind !== "actionReference" ||
        source.recordList.recordList.actionReference.instanceId !==
          sourceInstanceId
      ) {
        continue;
      }
      this.setSourceAt(id, {
        ...source,
        recordList: {
          ...source.recordList,
          metadata: {
            ...source.recordList.metadata,
            applicationId: table.applicationId,
            ...(table.solutionId === undefined
              ? {}
              : { solutionId: table.solutionId }),
          },
        },
      });
    }
  }

  @action
  clearSourceIfActionReference(actionReference: ActionReference): void {
    for (const id of this.allLoopInstanceIds()) {
      const source = this.sourceAt(id);
      if (!source) continue;

      const matches =
        (source.oneofKind === "recordList" &&
          source.recordList.recordList.oneofKind === "actionReference" &&
          source.recordList.recordList.actionReference.instanceId ===
            actionReference.instanceId) ||
        (source.oneofKind === "outputReference" &&
          source.outputReference.source.oneofKind === "actionReference" &&
          source.outputReference.source.actionReference.instanceId ===
            actionReference.instanceId);

      if (matches) {
        this.setSourceAt(id, undefined);
        // Drop `currentItem` pills/inputs inside loop actions — they derive
        // from the (now-gone) loop source action's outputs.
        this.setActionsWithOutReferencesAt(
          id,
          undefined,
          actionReference,
          CURRENT_ITEM_OUTPUT_ID,
        );
      }
    }
  }

  /**
   * Removes references from loop actions
   * @param reference - Reference to remove
   * @param whatActionReferencesRemove - Action reference to remove
   */
  @action
  setActionsWithOutReferences(
    reference?: string,
    whatActionReferencesRemove?: ActionReference,
    outputIdToRemove?: string,
  ): void {
    const actions = this.loopActions.map((action) =>
      ejectReferences(
        action,
        reference,
        whatActionReferencesRemove,
        outputIdToRemove,
      ),
    );

    this.handleGenPdfReferences(actions, whatActionReferencesRemove);
    this.setLoopActions(actions);
  }

  /** Same as setActionsWithOutReferences, but for the loop addressed by
   * instanceId (anywhere in the tree, including loops nested inside a
   * loop). Needed so cleanups triggered from a nested loop's own UI don't
   * fall through to the singleton root loop's actions.
   *
   * Covers every action in that loop's OWN Current Item scope: its direct
   * actions and the actions of Conditional Logic branches nested inside it at
   * any depth -- those may hold currentItem pills too. Loops nested inside it
   * own their own scope and are left alone. */
  @action
  setActionsWithOutReferencesAt(
    instanceId: number,
    reference?: string,
    whatActionReferencesRemove?: ActionReference,
    outputIdToRemove?: string,
  ): void {
    // Addressed through the wide candidate type on purpose: LoopActionGroup
    // narrows a loop's actionGroups to "actions" groups only, but a loop body
    // also holds branches groups, which is exactly what is walked here.
    const loopGroup = findGroupByInstanceId(this.self.actionGroups, instanceId);
    if (loopGroup?.config.oneofKind !== "loop") return;

    const actions: ActionCandidate[] = [];
    const actionGroups = mapScopeActionsDeep(
      loopGroup.config.loop.actionGroups,
      (action) => {
        const next = ejectReferences(
          action,
          reference,
          whatActionReferencesRemove,
          outputIdToRemove,
        );
        actions.push(next);
        return next;
      },
    );

    // Mutates the mapped actions in place, so it must run BEFORE they are
    // assigned into the observable tree (MobX clones plain objects on the
    // way in).
    this.handleGenPdfReferences(actions, whatActionReferencesRemove);
    loopGroup.config.loop.actionGroups = actionGroups;
  }

  /**
   * Sets the name for the loop
   * @param name - Name to set for the loop
   */
  @action
  setLoopName(name: string): void {
    const loopGroup = this.loopActionGroup;
    if (!this.isValidLoopActionGroup(loopGroup)) return;

    loopGroup.config.loop.name = name;
  }

  /**
   * Every loop instanceId anywhere in the tree (root-level and nested at any
   * depth), so `clearSourceIfTriggerReference`/`clearSourceIfActionReference`
   * can scrub a stale source from EVERY loop, not just the singleton root
   * loop (`collectInstanceIdsDeep` also yields action instanceIds; those are
   * filtered out here since `loopGroupById` only resolves loop groups).
   */
  private allLoopInstanceIds(): number[] {
    return collectInstanceIdsDeep(this.self.actionGroups).filter(
      (id) => this.loopGroupById(id) !== undefined,
    );
  }

  /**
   * Handles references for gen-pdf actions
   * @param actions - Array of actions to process
   * @param whatActionReferencesRemove - Action reference to remove
   */
  private handleGenPdfReferences(
    actions: ActionCandidate[],
    whatActionReferencesRemove?: ActionReference,
  ): void {
    actions.forEach((action) => {
      if (!this.isGenPdfAction(action, whatActionReferencesRemove)) return;

      action.recordList = undefined;
      const docInput = action.inputs.find(
        (input) => input.inputId === "export-type",
      );
      if (docInput) {
        docInput.value = undefined;
      }
    });
  }

  /**
   * Type guard for gen-pdf actions
   * @param action - Action to check
   * @param whatActionReferencesRemove - Action reference to check against
   * @returns True if action is a gen-pdf action with matching reference
   */
  private isGenPdfAction(
    action: ActionCandidate,
    whatActionReferencesRemove?: ActionReference,
  ): boolean {
    return (
      action.actionReference?.actionId === "gen-pdf" &&
      action.recordList?.recordList !== undefined &&
      action.recordList?.recordList.oneofKind === "actionReference" &&
      whatActionReferencesRemove !== undefined &&
      compareActionReferences(
        action.recordList.recordList.actionReference,
        whatActionReferencesRemove,
      )
    );
  }

  /**
   * Type guard for loop action groups
   * @param group - Action group to check
   * @returns True if group is a valid loop action group
   */
  private isValidLoopActionGroup(
    group?: ActionGroupCandidate,
  ): group is LoopActionGroup {
    return (
      !!group &&
      group.config.oneofKind === "loop" &&
      group.config.loop.actionGroups[0]?.config.oneofKind === "actions"
    );
  }

  /**
   * Body of getDefaultGroupName/defaultGroupNameAt, extracted so it can be
   * addressed either at the singleton root loop or at an arbitrary
   * (possibly nested) loop group.
   */
  private defaultGroupNameFor(loopGroup: LoopActionGroup | undefined): {
    groupName: string;
    isSourceSet: boolean;
  } {
    const source =
      loopGroup?.config.oneofKind === "loop"
        ? loopGroup.config.loop.source
        : undefined;

    if (source?.oneofKind === "recordList") {
      // resolve against the loop's own upstream execution path — the same
      // set the source picker offers (branches.upstreamActionsOf), so a
      // find-records earlier in the loop's branch validates while a sibling
      // branch's one doesn't; root-only groupActions would miss both
      const upstreamActions =
        loopGroup !== undefined
          ? this.candidateStore.branches.upstreamActionsOf(loopGroup.instanceId)
          : [];
      const findRecordsActions = upstreamActions.filter(
        (action) =>
          action.actionReference?.actionId === ActionLabel.findRecords,
      );
      const selectedFindRecordsAction = recordListInputConfigToOptionValue(
        source?.recordList,
      );

      if (!findRecordsActions.length || !selectedFindRecordsAction) {
        return { groupName: "", isSourceSet: false };
      }

      const actionOptions = findRecordsActionsToOptions(findRecordsActions);
      const tableName =
        actionOptions.find(
          (option) => option.value === selectedFindRecordsAction,
        )?.application?.name ?? "";

      return {
        groupName: tableName
          ? `${String(t("general.repeat-for-each-in"))} "${tableName}"`
          : "",
        isSourceSet: !!tableName,
      };
    }

    if (source?.oneofKind === "outputReference") {
      const outputId = source.outputReference.outputId;

      switch (source.outputReference.source.oneofKind) {
        case "actionReference": {
          const instanceId =
            source.outputReference.source.actionReference.instanceId;
          const webhookAction = this.candidateStore.allActions.find(
            (action) => action.actionReference?.instanceId === instanceId,
          );
          const output = webhookAction
            ? getOutputDescriptions(webhookAction).find(
                (output) => output.outputId === outputId,
              )
            : undefined;
          return {
            groupName: output?.label
              ? `${String(t("general.repeat-for-each-in"))} ${output.label}`
              : "",
            isSourceSet: !!output?.label,
          };
        }
        case "triggerReference": {
          const trigger = this.candidateStore.self.trigger;
          const triggerOutput = getOutputDescriptions(trigger).find(
            (output) => output.outputId === outputId,
          );
          return {
            groupName: triggerOutput?.label
              ? `${String(t("general.repeat-for-each-in"))} ${
                  triggerOutput.label
                }`
              : "",
            isSourceSet: !!triggerOutput?.label,
          };
        }
        // we doesn't support loopItem yet
        case "loopItem":
        default:
          return { groupName: "", isSourceSet: false };
      }
    }

    return { groupName: "", isSourceSet: false };
  }

  /**
   * Body of isConfiguredSourceValid/isConfiguredSourceValidAt, extracted so
   * it can be addressed either at the singleton root loop or at an
   * arbitrary (possibly nested) loop group.
   */
  private isConfiguredSourceValidFor(
    loopGroup: LoopActionGroup | undefined,
  ): boolean {
    const source =
      loopGroup?.config.oneofKind === "loop"
        ? loopGroup.config.loop.source
        : undefined;
    const loopActions = this.isValidLoopActionGroup(loopGroup)
      ? loopGroup.config.loop.actionGroups[0].config.actions.actions
      : [];

    // A loop whose only content is an Advanced Logic block (Conditional Logic
    // or a nested Loop) is NOT empty: that block is valid content even with no
    // plain action before it. Mirrors branches.loopHasContent -- without this
    // the loop wrongly shows a warning icon (STR-14151), because loopActions
    // only covers the flat actions group, not the sibling advanced-logic groups.
    const hasNestedAdvancedLogic =
      loopGroup?.config.oneofKind === "loop" &&
      loopGroup.config.loop.actionGroups.some(
        (group) => group.config.oneofKind !== "actions",
      );

    const isEmpty = loopActions.length === 0 && !hasNestedAdvancedLogic;
    const isSourceSet = this.defaultGroupNameFor(loopGroup).isSourceSet;
    const isError =
      loopActions.some((a) => a.deletedError) || isEmpty || !isSourceSet;

    const isWebhookSource = Boolean(
      source?.oneofKind === "outputReference" &&
        source.outputReference.outputId &&
        ((source?.outputReference?.source?.oneofKind === "actionReference" &&
          source?.outputReference?.source?.actionReference?.instanceId) ||
          source?.outputReference?.source?.oneofKind === "triggerReference"),
    );

    if (isWebhookSource) {
      const selectedOutput =
        source?.oneofKind === "outputReference"
          ? source.outputReference.outputId
          : null;
      const outputs = outputReferenceToOptionValue(
        { source } as unknown as LoopingStore,
        this.candidateStore.self.trigger,
        this.candidateStore.allActions,
      );

      return (
        !!outputs?.some((output) => output.outputId === selectedOutput) &&
        !isError
      );
    }

    return !isError;
  }

  /**
   * Gets the default group name based on find records action
   * @returns Object containing group name and whether find is set
   */
  getDefaultGroupName(): {
    groupName: string;
    isSourceSet: boolean;
  } {
    return this.defaultGroupNameFor(this.loopActionGroup);
  }

  /** Same as getDefaultGroupName, but for the loop addressed by instanceId. */
  defaultGroupNameAt(instanceId: number): {
    groupName: string;
    isSourceSet: boolean;
  } {
    return this.defaultGroupNameFor(this.loopGroupById(instanceId));
  }

  /** Gets the loop name */
  get loopName(): string {
    const loopGroup = this.loopActionGroup;
    return loopGroup?.config.oneofKind === "loop"
      ? loopGroup.config.loop.name
      : "";
  }

  /** Gets the record list configuration */
  get source(): LoopRecordListActionGroupConfig["source"] | undefined {
    const loopGroup = this.loopActionGroup;
    return loopGroup?.config.oneofKind === "loop"
      ? loopGroup.config.loop.source
      : undefined;
  }

  /** Gets the loop actions */
  get loopActions(): ActionCandidate[] {
    const loopGroup = this.loopActionGroup;
    return this.isValidLoopActionGroup(loopGroup)
      ? loopGroup.config.loop.actionGroups[0].config.actions.actions
      : [];
  }

  get isWebhookSource(): boolean {
    return Boolean(
      this.source?.oneofKind === "outputReference" &&
        this.source.outputReference.outputId &&
        ((this.source?.outputReference?.source?.oneofKind ===
          "actionReference" &&
          this.source?.outputReference?.source?.actionReference?.instanceId) ||
          this.source?.outputReference?.source?.oneofKind ===
            "triggerReference"),
    );
  }

  get isConfiguredSourceValid(): boolean {
    return this.isConfiguredSourceValidFor(this.loopActionGroup);
  }

  /** Same as isConfiguredSourceValid, but for the loop addressed by instanceId. */
  isConfiguredSourceValidAt(instanceId: number): boolean {
    return this.isConfiguredSourceValidFor(this.loopGroupById(instanceId));
  }
}
